import React, { useEffect, useRef } from "react";
import gsap from "gsap";

const OrbitalCanvas = () => {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: 0.5, y: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationId;
    let time = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX / window.innerWidth;
      mouse.current.y = e.clientY / window.innerHeight;
    };
    window.addEventListener("mousemove", handleMouseMove);

    // Orbit definitions
    const orbits = [
      {
        // Inner — fast, accent
        radiusX: 0.3,
        radiusY: 0.12,
        rotation: -0.18,
        speed: 0.000006,
        offset: 0,
        ringAlpha: 0.06,
        dotSize: 2.5,
        dotColor: [226, 69, 39],
        glowSize: 40,
        glowAlpha: 0.15,
        trailLength: 120,
        trailAlpha: 0.03,
        parallaxFactor: 0.3,
      },
      {
        // Middle — reverse, cream
        radiusX: 0.42,
        radiusY: 0.1,
        rotation: 0.25,
        speed: -0.000004,
        offset: Math.PI * 0.7,
        ringAlpha: 0.00004,
        dotSize: 2,
        dotColor: [232, 224, 212],
        glowSize: 30,
        glowAlpha: 0.1,
        trailLength: 90,
        trailAlpha: 0.02,
        parallaxFactor: 0.5,
      },
      {
        // Outer — slow, faint
        radiusX: 0.55,
        radiusY: 0.08,
        rotation: 0.06,
        speed: 0.0000025,
        offset: Math.PI * 1.4,
        ringAlpha: 0.025,
        dotSize: 1.5,
        dotColor: [255, 255, 255],
        glowSize: 20,
        glowAlpha: 0.06,
        trailLength: 60,
        trailAlpha: 0.012,
        parallaxFactor: 0.7,
      },
    ];

    // Get point on ellipse
    const getPoint = (orbit, angle, w, h, mx, my) => {
      const px = (mx - 0.5) * orbit.parallaxFactor * 40;
      const py = (my - 0.5) * orbit.parallaxFactor * 25;
      const cos = Math.cos(orbit.rotation);
      const sin = Math.sin(orbit.rotation);
      const rx = orbit.radiusX * w;
      const ry = orbit.radiusY * h;
      const ex = rx * Math.cos(angle);
      const ey = ry * Math.sin(angle);
      return {
        x: w / 2 + (ex * cos - ey * sin) + px,
        y: h / 2 + (ex * sin + ey * cos) + py,
      };
    };

    // Draw a full ellipse path
    const drawEllipse = (orbit, w, h, mx, my) => {
      ctx.beginPath();
      const steps = 200;
      for (let i = 0; i <= steps; i++) {
        const angle = (i / steps) * Math.PI * 2;
        const p = getPoint(orbit, angle, w, h, mx, my);
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(255, 255, 255, ${orbit.ringAlpha})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    };

    // Draw dot trail
    const drawTrail = (orbit, currentAngle, w, h, mx, my) => {
      const steps = orbit.trailLength;
      const angleStep =
        (Math.sign(orbit.speed) || 1) * ((Math.PI * 2) / 800);

      for (let i = steps; i >= 0; i--) {
        const angle = currentAngle - i * angleStep;
        const p = getPoint(orbit, angle, w, h, mx, my);
        const alpha =
          orbit.trailAlpha * (1 - i / steps) * (1 - i / steps);
        const size = orbit.dotSize * (1 - i / steps * 0.6);
        const [r, g, b] = orbit.dotColor;

        ctx.beginPath();
        ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${alpha})`;
        ctx.fill();
      }
    };

    // Draw main dot with glow
    const drawDot = (orbit, angle, w, h, mx, my) => {
      const p = getPoint(orbit, angle, w, h, mx, my);
      const [r, g, b] = orbit.dotColor;

      // Outer glow
      const gradient = ctx.createRadialGradient(
        p.x,
        p.y,
        0,
        p.x,
        p.y,
        orbit.glowSize
      );
      gradient.addColorStop(
        0,
        `rgba(${r}, ${g}, ${b}, ${orbit.glowAlpha})`
      );
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.beginPath();
      ctx.arc(p.x, p.y, orbit.glowSize, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Core dot
      ctx.beginPath();
      ctx.arc(p.x, p.y, orbit.dotSize, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.9)`;
      ctx.fill();
    };

    // Animation loop
    const animate = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const mx = mouse.current.x;
      const my = mouse.current.y;

      ctx.clearRect(0, 0, w, h);

      orbits.forEach((orbit) => {
        const angle = time * orbit.speed * 1000 + orbit.offset;

        drawEllipse(orbit, w, h, mx, my);
        drawTrail(orbit, angle, w, h, mx, my);
        drawDot(orbit, angle, w, h, mx, my);
      });

      time++;
      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ display: "block" }}
    />
  );
};

const OrbitalBackground = () => {
  const wrapperRef = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      wrapperRef.current,
      { opacity: 0 },
      { opacity: 1, duration: 2.5, delay: 0.5, ease: "power2.out" }
    );
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="absolute inset-0 w-full h-full overflow-hidden pointer-events-none z-0 opacity-0"
    >
      {/* Base gradient */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_50%_50%,_#111210_0%,_#0a0908_45%,_#050505_100%)]" />

      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='250'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Ambient warm glow */}
      <div className="absolute top-[42%] left-[38%] -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-brand-accent/[0.015] rounded-full blur-[200px]" />
      <div className="absolute top-[55%] left-[58%] -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-brand-cream/[0.01] rounded-full blur-[160px]" />

      {/* Canvas orbital system */}
      <OrbitalCanvas />

      {/* 4-edge vignette */}
      <div className="absolute top-0 left-0 w-full h-52 bg-gradient-to-b from-[#050505] via-[#050505]/80 to-transparent" />
      <div className="absolute bottom-0 left-0 w-full h-52 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-transparent" />
      <div className="absolute top-0 left-0 w-52 h-full bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
      <div className="absolute top-0 right-0 w-52 h-full bg-gradient-to-l from-[#050505] via-[#050505]/60 to-transparent" />
    </div>
  );
};

export default OrbitalBackground;